import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../../store/useAuthStore";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Checkbox, IconButton, Snackbar, Alert, Skeleton,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem,
} from "@mui/material";
import { MdDelete, MdEdit, MdArrowBack } from "react-icons/md";

const Categories = () => {
  const { token } = useAuthStore();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = add, object = edit
  const [form, setForm] = useState({ name: "", description: "", parentCategory: "" });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/getAllInventoryCategories", { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (res.ok) setCategories(data.categories || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchCategories(); }, []);

  const openAdd = () => {
    setEditing(null); setForm({ name: "", description: "", parentCategory: "" });
    setFormError(""); setDialogOpen(true);
  };

  const openEdit = (cat) => {
    setEditing(cat);
    setForm({
      name: cat.name || "",
      description: cat.description || "",
      parentCategory: cat.parentCategory?._id || cat.parentCategory || "",
    });
    setFormError(""); setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setFormError("Name is required"); return; }
    setSaving(true); setFormError("");
    try {
      const url = editing
        ? `/api/updateInventoryCategory/${editing._id}`
        : "/api/addInventoryCategory";
      const method = editing ? "PUT" : "POST";
      const body = {
        name: form.name.trim(),
        description: form.description,
        parentCategory: form.parentCategory || null,
      };
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { setFormError(data.error || "Save failed"); setSaving(false); return; }
      setDialogOpen(false);
      setSnackbar({ open: true, message: editing ? "Category updated" : "Category added", severity: "success" });
      fetchCategories();
    } catch (err) {
      setFormError("Save failed: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`Delete ${selected.length} categorie(s)? This cannot be undone.`)) return;
    try {
      const res = await fetch("/api/deleteInventoryCategory", {
        method: "DELETE",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ids: selected }),
      });
      const data = await res.json();
      if (res.ok) {
        setSnackbar({ open: true, message: `${data.deletedCount} categorie(s) deleted`, severity: "success" });
        setSelected([]); fetchCategories();
      } else {
        setSnackbar({ open: true, message: data.error, severity: "error" });
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to delete", severity: "error" });
    }
  };

  return (
    <div className="m-2 md:m-6 p-2 md:p-6">
      <div className="flex justify-between items-center px-4 mb-4 bg-white shadow rounded-lg h-[70px]">
        <div className="flex items-center gap-2">
          <IconButton onClick={() => navigate("/StockMaster")} size="small"><MdArrowBack /></IconButton>
          <h2 className="text-xl font-semibold text-gray-800">Inventory Categories</h2>
        </div>
        <div className="flex gap-2">
          {selected.length > 0 && (
            <button onClick={handleDelete} className="flex items-center gap-1 bg-red-500 text-white px-3 py-2 rounded-lg text-sm hover:bg-red-600">
              <MdDelete /> Delete ({selected.length})
            </button>
          )}
          <button onClick={openAdd} className="bg-primary text-white px-4 py-2 rounded-lg hover:opacity-90">+ New Category</button>
        </div>
      </div>

      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selected.length > 0 && selected.length < categories.length}
                    checked={categories.length > 0 && selected.length === categories.length}
                    onChange={(e) => setSelected(e.target.checked ? categories.map((c) => c._id) : [])}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Parent</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? [...Array(5)].map((_, i) => (
                <TableRow key={i}>{[...Array(5)].map((_, j) => <TableCell key={j}><Skeleton animation="wave" /></TableCell>)}</TableRow>
              )) : categories.length === 0 ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}><p className="text-gray-400">No categories yet</p></TableCell></TableRow>
              ) : categories.map((cat) => (
                <TableRow key={cat._id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selected.includes(cat._id)}
                      onChange={() => setSelected((p) => p.includes(cat._id) ? p.filter((s) => s !== cat._id) : [...p, cat._id])}
                    />
                  </TableCell>
                  <TableCell><span className="font-medium">{cat.name}</span></TableCell>
                  <TableCell>{cat.parentCategory?.name || "-"}</TableCell>
                  <TableCell className="text-gray-600">{cat.description || "-"}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => openEdit(cat)}><MdEdit className="text-gray-600" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? "Edit Category" : "New Category"}</DialogTitle>
        <DialogContent dividers>
          {formError && <Alert severity="error" className="mb-3">{formError}</Alert>}
          <TextField
            label="Name" fullWidth required margin="normal" size="small"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <TextField
            select label="Parent Category" fullWidth margin="normal" size="small"
            value={form.parentCategory}
            onChange={(e) => setForm({ ...form, parentCategory: e.target.value })}
          >
            <MenuItem value="">(none)</MenuItem>
            {categories
              .filter((c) => !editing || c._id !== editing._id)
              .map((c) => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
          </TextField>
          <TextField
            label="Description" fullWidth margin="normal" size="small" multiline rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : (editing ? "Update" : "Create")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: "100%" }}>{snackbar.message}</Alert>
      </Snackbar>
    </div>
  );
};

export default Categories;
